/**
 * Reusable exposed value tiles/grid for module dashboards.
 * Provide `translationPrefix` (e.g. "mod_energy.energy_management") for i18n keys.
 */

import { BarChart3 } from "lucide-react";
import { useLanguage } from "@/core/LanguageContext";
import { Button } from "@/shared/ui/button";
import { formatValue } from "@/core/lib/exposedValues";
import type { ExposedValueDefinition, ExposedValueMapping, LocationScope, RealTimeValue } from "@/core/types/exposed-values";

interface ExposedValueCardProps {
  translationPrefix: string;
  definition: ExposedValueDefinition;
  mapping: ExposedValueMapping | null;
  realTimeValues: Record<string, RealTimeValue>;
  onSelect: () => void;
  onOpenChart: (mapping: ExposedValueMapping) => void;
  disabled?: boolean;
  compact?: boolean;
}

export const ExposedValueTile = ({
  translationPrefix,
  definition,
  mapping,
  realTimeValues,
  onSelect,
  onOpenChart,
  disabled = false,
  compact = false,
}: ExposedValueCardProps) => {
  const { t } = useLanguage();
  const value = mapping ? realTimeValues[mapping.field_mapping_id]?.value : null;
  const unitSymbol = mapping?.field_mapping?.datapoint?.unit?.symbol;
  const hasMapping = Boolean(mapping);

  return (
    <div className={`rounded-lg border p-3 ${compact ? "bg-muted/30" : "bg-muted/40"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">{definition.display_name}</p>
          <p className={`text-lg font-semibold ${hasMapping ? "" : "text-muted-foreground"}`}>
            {formatValue(value)}
            {unitSymbol && <span className="text-xs font-normal ml-1">{unitSymbol}</span>}
          </p>
        </div>
        {hasMapping ? (
          <Button variant="ghost" size="icon" onClick={() => mapping && onOpenChart(mapping)}>
            <BarChart3 className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={disabled} onClick={onSelect}>
            {t(`${translationPrefix}.metric.selectDatapoint`, "Select datapoint")}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {mapping?.field_mapping?.datapoint?.display_name ??
          t(`${translationPrefix}.metric.noMapping`, "No mapping")}
      </p>
    </div>
  );
};

interface ExposedValueGridProps {
  translationPrefix: string;
  definitions: ExposedValueDefinition[];
  scope: LocationScope;
  getMapping: (scope: LocationScope, exposedValueId: string) => ExposedValueMapping | null;
  realTimeValues: Record<string, RealTimeValue>;
  onSelect: (definition: ExposedValueDefinition) => void;
  onOpenChart: (mapping: ExposedValueMapping) => void;
  isSelectionDisabled?: boolean;
}

export const ExposedValueGrid = ({
  translationPrefix,
  definitions,
  scope,
  getMapping,
  realTimeValues,
  onSelect,
  onOpenChart,
  isSelectionDisabled = false,
}: ExposedValueGridProps) => {
  const { t } = useLanguage();

  if (definitions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(`${translationPrefix}.grid.empty`, "No exposed values defined.")}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {definitions.map((definition) => (
        <ExposedValueTile
          key={definition.id}
          translationPrefix={translationPrefix}
          definition={definition}
          mapping={getMapping(scope, definition.id)}
          realTimeValues={realTimeValues}
          onSelect={() => onSelect(definition)}
          onOpenChart={onOpenChart}
          disabled={isSelectionDisabled}
          compact
        />
      ))}
    </div>
  );
};
